import React, { useState, useEffect } from 'react';

import { Heading } from '../../components';

import { FaUser, FaEnvelope, FaPhoneAlt, FaCalendar, FaClock, FaCreditCard, FaAngleDown, FaComment, FaCar, FaGlobe, FaIdCard, FaCalendarAlt } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';

import axios from 'axios';

// API endpoint for submitting the booking form
const API_PATH = 'https://elitecars-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const BookingForm = ({ formType }) => {

  // State to manage form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    dateOfBirth: '',
    licenseNumber: '',
    licenseCountry: '',
    pickupLocation: '',
    dropoffLocation: '',
    pickupDate: '',
    pickupTime: '',
    dropoffDate: '',
    dropoffTime: '',
    preferredCarModel: '',
    carType: '',
    paymentMethod: '',
    instructions: '',
    extraServices: []
  });

  // State for form submission status
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect to automatically hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  /**
   * Handles form submission with validation and API call
   * @param {Event} e - Form submit event
   */
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {

      // Validate all required fields
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.dateOfBirth.trim()) validationErrors.push('Date of birth is required');
      if (!formData.licenseNumber.trim()) validationErrors.push('License number is required');
      if (!formData.licenseCountry.trim()) validationErrors.push('Country of issuance is required');
      if (!formData.pickupLocation.trim()) validationErrors.push('Pickup location is required');
      if (!formData.dropoffLocation.trim()) validationErrors.push('Dropoff location is required');
      if (!formData.pickupDate.trim()) validationErrors.push('Pickup date is required');
      if (!formData.pickupTime.trim()) validationErrors.push('Pickup time is required');
      if (!formData.dropoffDate.trim()) validationErrors.push('Dropoff date is required');
      if (!formData.dropoffTime.trim()) validationErrors.push('Dropoff time is required');
      if (!formData.carType) validationErrors.push('Car type is required');
      if (!formData.paymentMethod) validationErrors.push('Payment method is required');

      // If validation errors exist, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => postData.append(`${key}[]`, item));
        } else {
          postData.append(key, value);
        }
      });

      // Submit data to API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Booking request submission failed');
      }

      // Reset form on success
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        dateOfBirth: '',
        licenseNumber: '',
        licenseCountry: '',
        pickupLocation: '',
        dropoffLocation: '',
        pickupDate: '',
        pickupTime: '',
        dropoffDate: '',
        dropoffTime: '',
        preferredCarModel: '',
        carType: '',
        paymentMethod: '',
        instructions: '',
        extraServices: []
      });
    } catch (err) {
      // Handle errors from API or validation
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles input changes for text fields
   * @param {Event} e - Input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Handles checkbox changes for extra services
   * @param {Event} e - Checkbox change event
   */
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, extraServices: [...prev.extraServices, value] };
      } else {
        return { ...prev, extraServices: prev.extraServices.filter(item => item !== value) };
      }
    });
  };

  // Define reusable input field components as variables
  const inputFields = {
    name: (
      <div className="input-field">
        <label htmlFor="name"><FaUser /></label>
        <input type="text" name="name" className="box" id="name" 
          value={formData.name} onChange={handleInputChange}
          placeholder="Name" disabled={isSubmitting} required />
      </div>
    ),
    email: (
      <div className="input-field">
        <label htmlFor="email"><FaEnvelope /></label>
        <input type="email" name="email" className="box" id="email" 
          value={formData.email} onChange={handleInputChange}
          placeholder="Email" disabled={isSubmitting} required />
      </div>
    ),
    phone: (
      <div className="input-field">
        <label htmlFor="number"><FaPhoneAlt /></label>
        <input type="number" name="number" className="box" id="number" 
          value={formData.number} onChange={handleInputChange}
          placeholder="Phone Number" disabled={isSubmitting} required />
      </div>
    ),
    dateOfBirth: (
      <div className="input-field">
        <label htmlFor="date-of-birth"><FaCalendarAlt /></label>
        <input
          type="text"
          name="dateOfBirth"
          placeholder="Date of Birth"
          id="date-of-birth"
          className="box"
          value={formData.dateOfBirth}
          onChange={handleInputChange}
          onFocus={(e) => (e.target.type = 'date')} // Switch to date picker on focus
          onBlur={(e) => (e.target.type = 'text')} // Switch back to text on blur
          disabled={isSubmitting}
          required
        />
      </div>
    ),
    licenseNumber: (
      <div className="input-field">
        <label htmlFor="licenseNumber"><FaIdCard /></label>
        <input
          type="text"
          name="licenseNumber"
          className="box"
          id="licenseNumber"
          value={formData.licenseNumber}
          onChange={handleInputChange}
          placeholder="Driver's License Number"
          disabled={isSubmitting}
          required
        />
      </div>
    ),
    licenseCountry: (
      <div className="input-field">
        <label htmlFor="licenseCountry"><FaGlobe /></label>
        <input
          type="text"
          name="licenseCountry"
          className="box"
          id="licenseCountry"
          value={formData.licenseCountry}
          onChange={handleInputChange}
          placeholder="Country of Issuance"
          disabled={isSubmitting}
          required
        />
      </div>
    ),
    pickupLocation: (
      <div className="input-field">
        <label htmlFor="pickup-location"><FaLocationDot /></label>
        <input type="text" name="pickupLocation" className="box" id="pickup-location" 
          value={formData.pickupLocation} onChange={handleInputChange}
          placeholder="Pickup Location" disabled={isSubmitting} required />
      </div>
    ),
    dropoffLocation: (
      <div className="input-field">
        <label htmlFor="dropoff-location"><FaLocationDot /></label>
        <input type="text" name="dropoffLocation" className="box" id="dropoff-location" 
          value={formData.dropoffLocation} onChange={handleInputChange}
          placeholder="Dropoff Location" disabled={isSubmitting} required />
      </div>
    ),
    pickupDate: (
      <div className="input-field">
        <label htmlFor="pickup-date"><FaCalendar /></label>
        <input type="text" name="pickupDate" placeholder="Pickup Date" id="pickup-date" className="box" 
          value={formData.pickupDate} onChange={handleInputChange}
          onFocus={(e) => (e.target.type = 'date')}
          onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
      </div>
    ),
    pickupTime: (
      <div className="input-field">
        <label htmlFor="pickup-time"><FaClock /></label>
        <input type="text" name="pickupTime" placeholder="Pickup Time" id="pickup-time" className="box" 
          value={formData.pickupTime} onChange={handleInputChange}
          onFocus={(e) => (e.target.type = 'time')}
          onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
      </div>
    ),
    dropoffDate: (
      <div className="input-field">
        <label htmlFor="dropoff-date"><FaCalendar /></label>
        <input type="text" name="dropoffDate" placeholder="Dropoff Date" id="dropoff-date" className="box" 
          value={formData.dropoffDate} onChange={handleInputChange}
          onFocus={(e) => (e.target.type = 'date')}
          onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
      </div>
    ),
    dropoffTime: (
      <div className="input-field">
        <label htmlFor="dropoff-time"><FaClock /></label>
        <input type="text" name="dropoffTime" placeholder="Dropoff Time" id="dropoff-time" className="box" 
          value={formData.dropoffTime} onChange={handleInputChange}
          onFocus={(e) => (e.target.type = 'time')}
          onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
      </div>
    ),
    preferredCarModel: (
      <div className="input-field">
        <label htmlFor="preferredCarModel"><FaCar /></label>
        <input
          type="text"
          name="preferredCarModel"
          className="box"
          id="preferredCarModel"
          value={formData.preferredCarModel}
          onChange={handleInputChange}
          placeholder="Preferred Model (Optional)"
          disabled={isSubmitting}
        />
      </div>
    ),
    carType: (
      <div className="select">
        <div className="select-container">
          <label htmlFor="car-type"><FaCar /></label>
          <select className="select-box" id="car-type" name="carType"
            value={formData.carType} onChange={handleInputChange}
            disabled={isSubmitting} required>
            <option value="">Car Type</option>
            <option value="Hatchback">Hatchback</option>
            <option value="Sedan">Sedan</option>
            <option value="Coupe">Coupe</option>
            <option value="Suv">SUV</option>
            <option value="Sports Car">Sports Car</option>
          </select>
          <div className="icon-container"><FaAngleDown className="icon" /></div>
        </div>
      </div>
    ),
    paymentMethod: (
      <div className="select">
        <div className="select-container">
          <label htmlFor="payment-method"><FaCreditCard /></label>
          <select className="select-box" id="payment-method" name="paymentMethod"
            value={formData.paymentMethod} onChange={handleInputChange}
            disabled={isSubmitting} required>
            <option value="">Payment Method</option>
            <option value="Credit Card">Credit Card</option>
            <option value="Cash">Cash</option>
            <option value="Mobile Payment">Mobile Payment</option>
          </select>
          <div className="icon-container"><FaAngleDown className="icon" /></div>
        </div>
      </div>
    ),
    instructions: (
      <div className="input-field text-area">
        <label htmlFor="instructions"><FaComment /></label>
        <textarea cols="30" rows="10" name="instructions" className="box" id="instructions" 
          value={formData.instructions} onChange={handleInputChange}
          placeholder="Special Instructions" disabled={isSubmitting}></textarea>
      </div>
    ),
  };

  // Define reusable extra services checkboxes as variables
  const extraServicesCheckboxes = (
    <div className="extra-services">
      {/* Additional Driver(s) Checkbox */}
      <div className="checkbox-label">
        <input type="checkbox" id="additional-drivers" name="extra-services" value="additional-drivers"
          checked={formData.extraServices.includes('additional-drivers')}
          onChange={handleCheckboxChange} disabled={isSubmitting} />
        <label htmlFor="additional-drivers">Driver</label>
        <div className="checkbox-indicator"></div>
      </div>

      {/* GPS Navigation Checkbox */}
      <div className="checkbox-label">
        <input type="checkbox" id="gps-navigation" name="extra-services" value="gps-navigation"
          checked={formData.extraServices.includes('gps-navigation')}
          onChange={handleCheckboxChange} disabled={isSubmitting} />
        <label htmlFor="gps-navigation">GPS Navigation</label>
        <div className="checkbox-indicator"></div>
      </div>

      {/* Child Seat Checkbox */}
      <div className="checkbox-label">
        <input type="checkbox" id="child-seat" name="extra-services" value="child-seat"
          checked={formData.extraServices.includes('child-seat')}
          onChange={handleCheckboxChange} disabled={isSubmitting} />
        <label htmlFor="child-seat">Child Seat</label>
        <div className="checkbox-indicator"></div>
      </div>
    </div>
  );

  // Define reusable submit button component
  const submitButton = (
    <button type="submit" className="btn" name="submit" id="submit-1" disabled={isSubmitting}>
      {isSubmitting ? 'Submitting...' : 'Rent Car'}
    </button>
  );

  // Render the form structure based on the `formType` prop
  const renderFormStructure = () => {
    if (formType === 'booking-page') {
      return (
        <>
          {/* Form heading */}
          <Heading title={'Rent a Car'} span={'Drive Your Way'} sideHeading linear />

          <div className="input-box">
            {inputFields.name}
            {inputFields.email}
            {inputFields.phone}
          </div>

          <div className="input-box">
            {inputFields.dateOfBirth}
            {inputFields.licenseNumber}
            {inputFields.licenseCountry}
          </div>

          <div className="input-box">
            {inputFields.pickupLocation}
            {inputFields.dropoffLocation}
            {inputFields.pickupDate}
          </div>

          <div className="input-box">
            {inputFields.pickupTime}
            {inputFields.dropoffDate}
            {inputFields.dropoffTime}
          </div>

          <div className="input-box">
            {inputFields.carType}
            {inputFields.preferredCarModel}
            {inputFields.paymentMethod}
          </div>

          {inputFields.instructions}
          {extraServicesCheckboxes}
          {submitButton}
        </>
      );
    } else if (formType === 'home-booking') {
      return (
        <>
          {/* Form heading */}
          <Heading title={'Rent a Car'} span={'Drive Your Way'} sideHeading linear />

          <div className="input-box">
            {inputFields.name}
            {inputFields.email}
            {inputFields.phone}
            {inputFields.dateOfBirth}
          </div>

          <div className="input-box">
            {inputFields.licenseNumber}
            {inputFields.licenseCountry}
            {inputFields.pickupLocation}
            {inputFields.dropoffLocation}
          </div>

          <div className="input-box">
            {inputFields.pickupDate}
            {inputFields.pickupTime}
            {inputFields.dropoffDate}
            {inputFields.dropoffTime}
          </div>

          <div className="input-box">
            {inputFields.carType}
            {inputFields.preferredCarModel}
            {inputFields.paymentMethod}
            <div className='form-btn'>{submitButton}</div>
          </div>

          {extraServicesCheckboxes}
        </>
      );
    }
  };

  return (
    <form className="dark-form" onSubmit={handleSubmitForm}>
      {renderFormStructure()}

      {error && (
        <div className="alert error">
          {error.split(' | ').map((msg, i) => (
            <span key={i}>{msg}</span>
          ))}
        </div>
      )}

      {mailSent && <div className="alert success"><span>Request submitted</span></div>}
    </form>
  );
};

export default BookingForm;